<template>
    <div v-if="hasPermissionTo('list user')">
        <div v-if="!show_only">
            <el-select v-model="user_id"
                       class="w-full"
                       :style="'width: ' + width"
                       :multiple="multiple"
                       :disabled="disabled"
                       :filter-method="filterOptions"
                       default-first-option
                       filterable
                       :clearable="clearable"
                       :placeholder="placeholder"
                       :size="size"
                       :collapse-tags="collapseTags"
                       data-testid="contact-details-owner-selector"
                       @clear="emitClearEvent"
                       @visible-change="emitVisibleChangeEvent"
                       @remove-tag="emitRemoveTagEvent"
                       autocomplete="off">
                <el-select-bulk-actions
                    v-if="showBulkActions && multiple"
                    custom-class="ml-2"
                    @selectAll="selectAll"
                    @clearSelection="clearSelection"
                />
                <el-option-group key="Users"
                                 label="Users"
                                 v-if="normalUsers && normalUsers.length > 0">
                    <el-option
                        v-for="(user, index) in normalUsers"
                        :key="`user-${index}-${user.id}`"
                        :label="user.name"
                        :value="user.id">
                        <div class="media">
                            <div class="media-body">
                                <label>{{ user.name }}</label>
                                <small>{{ user.email }} - {{ getLabel(user) }}</small>
                            </div>
                        </div>
                    </el-option>
                </el-option-group>
                <slot/>
                <el-option-group key="Extensions"
                                 label="Extensions"
                                 v-if="!hide_extensions && extensionUsers && extensionUsers.length > 0">
                    <el-option
                        v-for="(user, index) in extensionUsers"
                        :key="`ext-${index}-${user.id}`"
                        :label="user.name"
                        :value="user.id">
                        <div class="media">
                            <div class="media-body">
                                <label>{{ user.name }}</label>
                                <small
                                    v-if="user.type === User.TYPE_AI_AGENT"
                                    class="ai-effect-gradient-text"
                                >
                                    <sparkle-icon width="12" height="12" />
                                    {{ getLabel(user) }}
                                </small>
                                <small v-else>
                                    {{ getLabel(user) }}
                                </small>
                            </div>
                        </div>
                    </el-option>
                </el-option-group>
            </el-select>
        </div>
        <div v-if="show_only && selectedUser">
            <span>{{ selectedUser.name }}</span>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import SparkleIcon from '../../images/app-icons/sparkle-bold-icon'
import auth from '../auth'
import * as AnswerTypes from '../constants/answer-types'
import * as User from '../constants/user'
import { acl_mixin } from '../mixins'
import ElSelectBulkActions from './el-select-bulk-actions.vue'

export default {
    mixins: [acl_mixin],

    components: {
        SparkleIcon,
        ElSelectBulkActions
    },

    props: {
        value: {
            required: false,
            default: null
        },

        multiple: {
            type: Boolean,
            required: false,
            default: false
        },

        show_only: {
            default: false,
            type: Boolean,
            required: false
        },

        disabled: {
            default: false,
            type: Boolean,
            required: false
        },

        hide_extensions: {
            default: false,
            type: Boolean,
            required: false
        },

        collapseTags: {
            default: false,
        },

        size: {
            default: ''
        },

        width: {
            default: ''
        },

        placeholder: {
            default: 'Select'
        },

        clearable: {
            type: Boolean,
            required: false,
            default: true,
        },

        include_aloai_users: {
            type: Boolean,
            required: false,
            default: false,
        },

        excludeUsers: {
            type: Array|Number,
            required: false,
            default: () => []
        },

        userType: {
            type: Number,
            required: false,
            default: null
        },

        onlyActive: {
            type: Boolean,
            required: false,
            default: false,
        },

        showBulkActions: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data() {
        return {
            auth: auth,
            user_id: this.value,
            filtered_text: null,
            User,
        }
    },

    computed: {
        ...mapState({
            current_company: state => state.cache.current_company,
            users: state => state.users
        }),

        selectedUser() {
            if (this.user_id) {
                return this.users.find(user => user.id == this.user_id)
            }

            return null
        },

        usersToExclude() {
            if (this.excludeUsers === null || this.excludeUsers === undefined) {
                return []
            }
            return Array.isArray(this.excludeUsers)
                ? this.excludeUsers
                : [this.excludeUsers]
        },

        filteredUsers() {
            if (this.users) {
                let filtered_users = this.users.filter((user) =>
                    !(user.role_names && user.role_names.length === 1 && user.read_only_access)
                )

                // Apply user type filter if specified
                if (this.userType !== null) {
                    filtered_users = filtered_users.filter(user => user.type === this.userType)
                }

                // Apply user exclusion filter
                if (this.usersToExclude.length > 0) {
                    filtered_users = filtered_users.filter(user => !this.usersToExclude.includes(user.id))
                }

                if (this.filtered_text) {
                    return filtered_users.filter((user) =>
                        (user.name && user.name.toLowerCase().includes(this.filtered_text.toLowerCase())) ||
                        (user.phone_number && user.phone_number.includes(this.filtered_text)) ||
                        (user.email && user.email.toLowerCase().includes(this.filtered_text.toLowerCase()))
                    )
                }

                return filtered_users
            }

            return []
        },

        normalUsers() {
            const users = this.filteredUsers.filter((user) => !user.is_destination)
            return this.onlyActive
                ? users.filter((user) => user.enabled)
                : users;
        },

        extensionUsers() {
            let extension_users = this.filteredUsers.filter((user) => user.is_destination)

            if (!this.include_aloai_users) {
                extension_users = extension_users.filter((user) => user.type !== User.TYPE_AI_AGENT)
            }

            return this.onlyActive
                ? extension_users.filter((user) => user.enabled)
                : extension_users
        },
    },

    methods: {
        emitVisibleChangeEvent(visible) {
            this.filtered_text = null
            this.$emit('visible-change', visible)
        },

        emitRemoveTagEvent(removed_user_id) {
            this.$emit('remove-tag', removed_user_id)
        },

        emitClearEvent() {
            this.$emit('clear')
        },

        getLabel(user) {
            if (!user) {
                return
            }

            switch (user.answer_by) {
                case AnswerTypes.BY_PHONE_NUMBER:
                    if (user.type === User.TYPE_AI_AGENT) {
                        return 'AloAi Agent'
                    }

                    return 'Phone Number (' + user.phone_number + ')'
                case AnswerTypes.BY_BROWSER:
                    return 'Browser / Apps'
                case AnswerTypes.BY_IP_PHONE:
                    return 'SIP (IP Phone)'
                case AnswerTypes.BY_NONE:
                    return 'Will Not Answer'
            }
        },

        filterOptions(filterValue) {
            this.filtered_text = filterValue
        },

        selectAll() {
            const ids = this.normalUsers.map(user => user.id)

            if (!this.hide_extensions) {
                ids.push(...this.extensionUsers.map(user => user.id))
            }

            this.user_id = ids
        },

        clearSelection() {
            this.user_id = []
        },
    },

    watch: {
        value() {
            this.user_id = this.value
        },

        user_id(val) {
            if (this.value !== undefined && this.user_id !== this.value) {
                this.$emit('change', val)
            }
        }
    }
}
</script>
